import React from 'react';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import useForgotPasswordForm from '../../hooks/useForgotPasswordForm';
import SiteLogo from '../../components/SiteLogo';

const ForgotPasswordClassic = () => {
  const { t } = useTranslation('auth');
  const { isDark } = useTheme();
  const {
    step, email, code, password, confirmPassword,
    showPassword, showConfirmPassword, loading, error,
    resendTimer, codeInputRefs,
    setShowPassword, setShowConfirmPassword,
    handleEmailChange, handleSendCode,
    handleCodeChange, handleCodeKeyDown, handleCodePaste,
    handleVerifyCode,
    handlePasswordChange, handleConfirmPasswordChange,
    handleResetPassword, handleResendCode, goBackToEmail,
  } = useForgotPasswordForm();

  const inputClass = (hasError) =>
    `w-full h-[52px] px-4 bg-gray-100 dark:bg-white/[0.05] border rounded-xl text-gray-900 dark:text-white text-[15px] placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-white/30 focus:bg-gray-50 dark:focus:bg-white/[0.07] transition-all duration-200 ${
      hasError ? 'border-red-500/50' : 'border-gray-200 dark:border-white/10'
    }`;

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-0 mb-8">
      {[1, 2, 3].map((s, i) => (
        <React.Fragment key={s}>
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
              step >= s
                ? 'bg-gray-900 dark:bg-white text-white dark:text-black'
                : 'bg-gray-100 dark:bg-white/[0.08] text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-white/10'
            }`}
          >
            {step > s ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : s}
          </div>
          {i < 2 && (
            <div className={`w-10 h-[2px] transition-all duration-300 ${
              step > s ? 'bg-gray-900 dark:bg-white' : 'bg-gray-200 dark:bg-white/[0.08]'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const ErrorAlert = ({ message }) => {
    if (!message) return null;
    return (
      <div className="p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm">
        {message}
      </div>
    );
  };

  const renderStep1 = () => (
    <form onSubmit={handleSendCode} className="space-y-4">
      <div className="text-center mb-3">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('forgotPassword.title', 'Reset your password')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
          {t('forgotPassword.enterEmail', 'Enter your email to receive a reset code')}
        </p>
      </div>
      <div>
        <input
          type="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder={t('forgotPassword.emailPlaceholder', 'Email address')}
          className={inputClass(error)}
          required
        />
      </div>
      <ErrorAlert message={error} />
      <button
        type="submit"
        disabled={loading}
        className="w-full h-[52px] bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-black rounded-full font-semibold text-[15px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('forgotPassword.sending', 'Sending...')}
          </>
        ) : (
          t('forgotPassword.sendCode', 'Send Reset Code')
        )}
      </button>
    </form>
  );

  const renderStep2 = () => (
    <form onSubmit={handleVerifyCode} className="space-y-4">
      <div className="text-center mb-3">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('forgotPassword.verifyTitle', 'Check your email')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
          {t('forgotPassword.codeSentTo', 'We sent a code to')}{' '}
          <span className="text-gray-900 dark:text-white font-medium">{email}</span>
        </p>
      </div>
      <div className="flex justify-center gap-2.5 py-2">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (codeInputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleCodeKeyDown(index, e)}
            onPaste={index === 0 ? handleCodePaste : undefined}
            className="w-11 h-13 sm:w-12 sm:h-14 bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-lg font-semibold text-center focus:outline-none focus:border-gray-400 dark:focus:border-white/30 focus:bg-gray-50 dark:focus:bg-white/[0.07] transition-all duration-200"
          />
        ))}
      </div>
      <ErrorAlert message={error} />
      <button
        type="submit"
        disabled={loading}
        className="w-full h-[52px] bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-black rounded-full font-semibold text-[15px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('forgotPassword.verifying', 'Verifying...')}
          </>
        ) : (
          t('forgotPassword.verifyCode', 'Verify Code')
        )}
      </button>
      <div className="text-center">
        {resendTimer > 0 ? (
          <p className="text-gray-500 text-sm">
            <Trans
              i18nKey="forgotPassword.resendIn"
              values={{ seconds: resendTimer }}
              defaults="Resend in {{seconds}}s"
              components={{ b: <span className="text-gray-900 dark:text-white font-medium" /> }}
            />
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResendCode}
            disabled={loading}
            className="text-gray-700 dark:text-white hover:text-gray-500 dark:hover:text-gray-300 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {t('forgotPassword.resendCode', 'Resend code')}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={goBackToEmail}
        className="flex items-center justify-center gap-1.5 text-gray-500 text-sm hover:text-gray-900 dark:hover:text-white transition-colors mx-auto"
      >
        <ArrowLeft className="rtl:-scale-x-100"size={14} />
        {t('forgotPassword.changeEmail', 'Use different email')}
      </button>
    </form>
  );

  const renderStep3 = () => (
    <form onSubmit={handleResetPassword} className="space-y-4">
      <div className="text-center mb-3">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('forgotPassword.newPasswordTitle', 'Set new password')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
          {t('forgotPassword.newPasswordSubtitle', 'Choose a strong password for your account')}
        </p>
      </div>
      <div>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            placeholder={t('forgotPassword.newPasswordPlaceholder', 'New password')}
            className={`${inputClass(error)} pe-12`}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      <div>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
            placeholder={t('forgotPassword.confirmPasswordPlaceholder', 'Confirm password')}
            className={`${inputClass(error)} pe-12`}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      <ErrorAlert message={error} />
      <button
        type="submit"
        disabled={loading}
        className="w-full h-[52px] bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-black rounded-full font-semibold text-[15px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('forgotPassword.resetting', 'Resetting...')}
          </>
        ) : (
          t('forgotPassword.resetPassword', 'Reset Password')
        )}
      </button>
    </form>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0a] flex flex-col items-center relative overflow-hidden">
      {/* Gradient glow */}
      <div
        className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full opacity-10 dark:opacity-30 blur-[120px] pointer-events-none"
        style={{ background: `radial-gradient(circle, var(--color-primary, #10b981) 0%, transparent 70%)` }}
      />
      <div
        className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-5 dark:opacity-15 blur-[100px] pointer-events-none"
        style={{ background: `radial-gradient(circle, var(--color-primary, #10b981) 0%, transparent 70%)` }}
      />

      {/* Logo + back */}
      <div className="w-full max-w-[480px] px-6 pt-12 sm:pt-16 pb-8 flex items-center justify-between relative z-10">
        <Link to="/login" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors">
          <ArrowLeft className="rtl:-scale-x-100"size={16} />
          {t('forgotPassword.backToLogin', 'Back')}
        </Link>
        <Link to="/">
          <SiteLogo className="h-9 sm:h-10" />
        </Link>
        <div className="w-16" />
      </div>

      {/* Form container */}
      <main className="w-full max-w-[480px] px-6 pb-12 relative z-10">
        <StepIndicator />
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}

        {/* Back to login */}
        <div className="text-center mt-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="rtl:-scale-x-100"size={14} />
            {t('forgotPassword.backToLogin', 'Back to Sign In')}
          </Link>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-3 mt-10">
          <a href="/page/terms-of-service" className="text-[11px] text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
            {t('register.termsOfService', 'Terms')}
          </a>
          <span className="text-gray-300 dark:text-gray-700 text-[11px]">&middot;</span>
          <a href="/page/privacy-policy" className="text-[11px] text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
            {t('register.privacyPolicy', 'Privacy')}
          </a>
          <span className="text-gray-300 dark:text-gray-700 text-[11px]">&middot;</span>
          <a href="/page/cookie-policy" className="text-[11px] text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
            {t('register.cookiePolicy', 'Cookies')}
          </a>
        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordClassic;
